/**
 * Banner di consenso cookie (GDPR) con preferenze granulari.
 *
 * Due parti indipendenti:
 *  - la BARRA in basso, mostrata solo finché l'utente non ha scelto (`!decided`):
 *    Accetta tutto / Rifiuta / Personalizza, più link all'informativa privacy.
 *  - il DIALOG preferenze, comandato dallo store (`prefsOpen`): può essere
 *    riaperto in QUALSIASI momento dopo la scelta — es. dalla pagina Privacy
 *    ("Gestisci preferenze cookie") — così il consenso è revocabile/modificabile
 *    facilmente quanto è stato dato (requisito GDPR).
 *
 * Alla scelta: salva il consenso (consentStore, persistito) e allinea il
 * Consent Mode di GA (updateConsent); se gli analitici sono accettati, avvia
 * GA (initAnalytics). Tutto no-op se GA non è attivo (fuori prod / senza ID).
 */

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Cookie } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { initAnalytics, updateConsent } from '@/lib/analytics'
import { useConsentStore } from './consentStore'

/** Applica la scelta a store + Consent Mode + (eventuale) avvio GA. */
function applyConsent(analytics: boolean, setConsent: (a: boolean) => void) {
  setConsent(analytics)
  updateConsent(analytics)
  if (analytics) initAnalytics()
}

export function CookieConsentBanner() {
  const decided = useConsentStore((s) => s.decided)
  const analytics = useConsentStore((s) => s.analytics)
  const prefsOpen = useConsentStore((s) => s.prefsOpen)
  const setConsent = useConsentStore((s) => s.setConsent)
  const setPrefsOpen = useConsentStore((s) => s.setPrefsOpen)

  // Stato locale del toggle nel dialog. Precompilato con la scelta corrente
  // (se già decisa): riaprendo le preferenze l'utente vede lo stato attuale.
  const [analyticsPref, setAnalyticsPref] = useState(analytics)

  // All'apertura del dialog, riallinea il toggle alla scelta salvata.
  useEffect(() => {
    if (prefsOpen) setAnalyticsPref(decided ? analytics : true)
  }, [prefsOpen, decided, analytics])

  return (
    <>
      {!decided && (
        <div className="fixed inset-x-0 bottom-0 z-50 border-t border-border bg-card/95 p-4 shadow-lg backdrop-blur supports-[backdrop-filter]:bg-card/80">
          <div className="mx-auto flex max-w-4xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <Cookie className="mt-0.5 h-5 w-5 shrink-0 text-secondary" />
              <p className="text-sm text-muted-foreground">
                Usiamo cookie tecnici necessari e, con il tuo consenso, cookie
                analitici (Google Analytics) per capire come viene usato il sito.{' '}
                <Link to="/privacy" className="font-semibold text-secondary hover:underline">
                  Informativa privacy
                </Link>
                .
              </p>
            </div>
            <div className="flex shrink-0 flex-col gap-2 sm:flex-row">
              <Button variant="outline" size="sm" onClick={() => setPrefsOpen(true)}>
                Personalizza
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => applyConsent(false, setConsent)}
              >
                Rifiuta
              </Button>
              <Button size="sm" onClick={() => applyConsent(true, setConsent)}>
                Accetta tutto
              </Button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={prefsOpen} onOpenChange={setPrefsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Preferenze cookie</DialogTitle>
            <DialogDescription>
              Scegli quali categorie attivare. I cookie necessari sono sempre
              attivi perché indispensabili al funzionamento del sito.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-4 py-2">
            <CategoryRow
              title="Necessari"
              description="Sessione, sicurezza e preferenze di base. Sempre attivi."
              value={true}
              disabled
            />
            <CategoryRow
              title="Analitici (Google Analytics)"
              description="Statistiche aggregate e anonime sull'uso del sito."
              value={analyticsPref}
              onChange={setAnalyticsPref}
            />
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                applyConsent(false, setConsent)
                setPrefsOpen(false)
              }}
            >
              Rifiuta tutto
            </Button>
            <Button
              size="sm"
              onClick={() => {
                applyConsent(analyticsPref, setConsent)
                setPrefsOpen(false)
              }}
            >
              Salva preferenze
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

/**
 * Riga categoria con toggle a due bottoni-segmento (Attivo / Disattivo): non
 * esiste un componente switch nel design system, riusiamo Tailwind + Button-like.
 */
function CategoryRow({
  title,
  description,
  value,
  onChange,
  disabled,
}: {
  title: string
  description: string
  value: boolean
  onChange?: (v: boolean) => void
  disabled?: boolean
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="flex flex-col gap-0.5">
        <span className="text-sm font-semibold text-foreground">{title}</span>
        <span className="text-xs text-muted-foreground">{description}</span>
      </div>
      <div className="flex shrink-0 overflow-hidden rounded-lg border border-input">
        {(['on', 'off'] as const).map((opt) => {
          const active = opt === 'on' ? value : !value
          return (
            <button
              key={opt}
              type="button"
              disabled={disabled}
              onClick={() => onChange?.(opt === 'on')}
              className={cn(
                'px-3 py-1.5 text-xs font-semibold transition-colors',
                active
                  ? 'bg-secondary text-secondary-foreground'
                  : 'bg-background text-muted-foreground hover:bg-accent',
                disabled && 'cursor-not-allowed opacity-60',
              )}
            >
              {opt === 'on' ? 'Attivo' : 'Disattivo'}
            </button>
          )
        })}
      </div>
    </div>
  )
}
